import React, { memo, ReactNode, useCallback, useEffect, useState } from 'react'
import {
    TableCell,
    Table as TableMui,
    TableRow,
    TableHead,
    Button,
    Checkbox,
    IconButton,
} from '@mui/material'
import {
    Grid as GridDx,
    Column,
    GridColumnExtension,
    SelectionState,
    IntegratedSelection,
} from '@devexpress/dx-react-grid'
import {
    Grid as GridMui,
    Table,
    TableFixedColumns,
    TableHeaderRow,
    TableSelection,
} from '@devexpress/dx-react-grid-material-ui'
import { GridColumn, GridProps, IndexItem } from './Grid.types'
import { Progress, ErrorTypography, RefreshIcon } from '../components'

function DataGrid<T>({
    getId: sourceGetId,
    items: sourceItems,
    columns: sourceColumns,
    selectable,
    clickable,
    refreshable,
    hoverable = false,
    empty,
    error,
    loading,
}: GridProps<T>) {
    const isSelectable = !!selectable
    const [items, setItems] = useState<IndexItem<T>[]>([])
    const [columns, setColumns] = useState<Column[]>([])
    const [columnExtensions, setColumnExtensions] = useState<
        GridColumnExtension[]
    >([])
    const [leftColumns, setLeftColumns] = useState<(string | symbol)[]>([])
    const [rightColumns, setFixedColumns] = useState<(string | symbol)[]>([])
    const [selection, setSelection] = useState<(string | number)[]>([])

    useEffect(() => {
        setItems(
            sourceItems.map((item: T, index: number) => {
                return { item, index } as IndexItem<T>
            })
        )
    }, [sourceItems])

    useEffect(() => {
        const cols = sourceColumns.map(
            (col: GridColumn<T>, indexCol: number) => {
                let title: ReactNode
                if (refreshable && refreshable.columnId === col.id) {
                    title = (
                        <IconButton size="small" onClick={refreshable.onClick}>
                            <RefreshIcon />
                        </IconButton>
                    )
                } else {
                    title = col.title
                }

                return {
                    name: col.id!,
                    title,
                    getCellValue: (row: any, columnName: string) => {
                        const { item, index } = row as IndexItem<T>
                        const content = col.converter(item, indexCol, index)
                        const disabled = clickable?.disabled
                            ? clickable.disabled(item)
                            : false

                        if (indexCol === 0 && !!clickable?.onClick) {
                            return (
                                <Button
                                    size="medium"
                                    color="primary"
                                    variant="text"
                                    disabled={disabled}
                                    onClick={() =>
                                        clickable.onClick(item, index)
                                    }
                                    sx={{ px: 0.5, py: 0.5, minWidth: 30 }}
                                >
                                    {content}
                                </Button>
                            )
                        }
                        return content
                    },
                } as Column
            }
        )

        let leftCols: (string | symbol)[] = sourceColumns
            .filter((col) => col.fixed === 'left')
            .map((col) => col.id!)

        if (isSelectable) {
            leftCols = [TableSelection.COLUMN_TYPE, ...leftCols]
        }

        const rightCols = sourceColumns
            .filter((col) => col.fixed === 'right')
            .map((col) => col.id!)

        const colExtensions = sourceColumns.map((col) => {
            return {
                columnName: col.id,
                width: col.width,
                align: col.textAlign,
                wordWrapEnabled: col.wordWrapEnabled,
            } as GridColumnExtension
        })

        setColumns(cols)
        setLeftColumns(leftCols)
        setFixedColumns(rightCols)
        setColumnExtensions(colExtensions)
    }, [sourceColumns, isSelectable, clickable, refreshable])

    useEffect(() => {
        setSelection(selectable?.selected ?? [])
    }, [selectable?.selected])

    const getId = useCallback(
        (item: IndexItem<T>): string => {
            return sourceGetId(item.item, item.index)
        },
        [sourceGetId]
    )

    const onSelectionChange = (selectedIds: (string | number)[]) => {
        if (isSelectable) {
            const ids = selectedIds.map((id) => `${id}`)
            const selectedItems = items
                .filter((indexItem: IndexItem<T>) => {
                    const id = getId(indexItem)
                    return ids.indexOf(id) >= 0
                })
                .map((item: IndexItem<T>) => item.item)

            selectable!.onChange(ids, selectedItems)
            setSelection(ids)
        }
    }

    return (
        <GridDx
            rows={items}
            columns={columns}
            getRowId={getId}
            rootComponent={GridMui.Root}
        >
            {selectable && (
                <SelectionState
                    selection={selection}
                    onSelectionChange={onSelectionChange}
                />
            )}
            {selectable && <IntegratedSelection />}
            <Table
                columnExtensions={columnExtensions}
                messages={{ noData: empty }}
                tableComponent={TableComponent}
                rowComponent={RowComponent(hoverable)}
                noDataRowComponent={NoDataRowComponent}
                noDataCellComponent={NoDataCell(loading, error)}
                headComponent={HeadComponent}
            />
            <TableHeaderRow />
            {selectable && (
                <TableSelection
                    showSelectAll
                    highlightRow
                    rowComponent={RowComponent(hoverable)}
                    showSelectionColumn
                    cellComponent={SelectionCell(false, selectable.disabled)}
                    headerCellComponent={SelectionCell(
                        true,
                        selectable.disabled
                    )}
                />
            )}
            {(leftColumns.length > 0 || rightColumns.length > 0) && (
                <TableFixedColumns
                    leftColumns={leftColumns}
                    rightColumns={rightColumns}
                />
            )}
        </GridDx>
    )
}

const TableComponent = (props: any) => {
    const { forwardedRef, children, style } = props
    return (
        <TableMui
            ref={forwardedRef}
            size="small"
            padding="normal"
            style={style}
        >
            {children}
        </TableMui>
    )
}

const HeadComponent = (props: any) => {
    const { forwardedRef, children } = props
    return (
        <TableHead
            ref={forwardedRef}
            sx={(theme) => {
                const color =
                    theme.palette.mode === 'dark'
                        ? theme.palette.common.black
                        : theme.palette.grey[100]
                return {
                    bgcolor: color,
                    '& .MuiTableCell-head': {
                        bgcolor: `${color} !important`,
                    },
                }
            }}
        >
            {children}
        </TableHead>
    )
}

const RowComponent =
    (hoverable: boolean) =>
    ({ forwardedRef, children, style, highlighted }: any) => {
        return (
            <TableRow
                ref={forwardedRef}
                style={style}
                hover={hoverable}
                selected={highlighted}
            >
                {children}
            </TableRow>
        )
    }

const NoDataRowComponent = (props: any) => {
    const { forwardedRef, children } = props
    return (
        <TableRow
            ref={forwardedRef}
            sx={(theme) => {
                return { height: theme.spacing(6) }
            }}
        >
            {children}
        </TableRow>
    )
}

const NoDataCell =
    (loading?: boolean, error?: string) => (cellProps: Table.NoDataCellProps) =>
        (
            <TableCell
                colSpan={cellProps.colSpan}
                rowSpan={cellProps.rowSpan}
                align="center"
            >
                {error && <ErrorTypography text={error} />}
                {loading && <Progress size="1.5rem" />}
                {!(error || loading) && cellProps.getMessage('noData')}
            </TableCell>
        )

const SelectionCell =
    (header: boolean, notAvailable?: string[]) => (cellProps: any) => {
        let checked,
            indeterminate,
            disabled: boolean | undefined = undefined

        if (header) {
            checked = cellProps.allSelected
            indeterminate = cellProps.someSelected
            disabled = notAvailable && notAvailable.length > 0
        } else {
            const id = cellProps.row?.item?.id
            checked = cellProps.selected
            disabled =
                cellProps.disabled ||
                notAvailable?.find((el) => el === id) !== undefined
        }

        return (
            <TableCell
                variant={header ? 'head' : 'body'}
                padding="none"
                colSpan={cellProps.colSpan}
                rowSpan={cellProps.rowSpan}
                className={cellProps.className}
                style={cellProps.style}
            >
                <Checkbox
                    size="medium"
                    checked={checked}
                    indeterminate={indeterminate}
                    disabled={disabled}
                    onChange={() => cellProps.onToggle()}
                />
            </TableCell>
        )
    }

export default memo(DataGrid)
